import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import OrgManager from '../services/OrgManager';

const PERIODS = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
];

export default function OrgActivityScreen() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg, myRole } = useOrg();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(7);
  const [error, setError] = useState('');

  const canView = myRole === 'owner' || myRole === 'admin';

  const load = useCallback(async () => {
    if (!activeOrg || !canView) return;
    setLoading(true);
    setError('');
    try {
      const data = await OrgManager.getActivity(activeOrg.id, sinceDays);
      setActivity(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeOrg, canView, sinceDays]);

  useEffect(() => { load(); }, [load]);

  if (!canView) return (
    <View style={styles.center}><Text style={{ color: theme.textSecondary }}>{t('app.org.activityAdminOnly')}</Text></View>
  );

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.background }]}
                contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.text }]}>{t('app.org.teamActivity')}</Text>
        <View style={styles.periodRow}>
          {PERIODS.map(p => (
            <TouchableOpacity
              key={p.days}
              onPress={() => setSinceDays(p.days)}
              style={[
                styles.periodBtn,
                { borderColor: theme.border },
                sinceDays === p.days && { backgroundColor: theme.accent, borderColor: theme.accent },
              ]}
            >
              <Text style={{ fontSize: 12, color: sinceDays === p.days ? '#fff' : theme.textSecondary }}>
                {p.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {loading ? (
        <Text style={{ color: theme.textSecondary }}>{t('app.org.loading')}</Text>
      ) : error ? (
        <Text style={{ color: '#ef4444' }}>{error}</Text>
      ) : activity.length === 0 ? (
        <Text style={{ color: theme.textSecondary, fontSize: 13 }}>{t('app.org.noActivity')}</Text>
      ) : (
        <View style={[styles.table, { backgroundColor: theme.cardBg, borderColor: theme.border }]}>
          <View style={[styles.tableHeader, { borderBottomColor: theme.border }]}>
            {[t('app.org.colMember'), t('app.org.colActed'), t('app.org.colDismissed'), t('app.org.colAssigned'), t('app.org.colLastActive')].map(h => (
              <Text key={h} style={[styles.colHeader, { color: theme.textSecondary }]}>{h}</Text>
            ))}
          </View>
          {activity.map((row, i) => (
            <View
              key={row.listener_id}
              style={[
                styles.tableRow,
                { borderBottomColor: theme.border },
                i % 2 === 1 && { backgroundColor: theme.border + '22' },
              ]}
            >
              <Text style={[styles.cell, { color: theme.text, fontWeight: '600' }]} numberOfLines={1}>
                @{row.username}
              </Text>
              <Text style={[styles.cell, { color: '#22c55e', fontWeight: '700' }]}>
                {row.actions_acted}
              </Text>
              <Text style={[styles.cell, { color: '#6b7280' }]}>
                {row.actions_dismissed}
              </Text>
              <Text style={[styles.cell, { color: '#8b5cf6' }]}>
                {row.actions_assigned}
              </Text>
              <Text style={[styles.cell, { color: theme.textSecondary, fontSize: 11 }]} numberOfLines={1}>
                {row.last_acted_at ? new Date(row.last_acted_at).toLocaleDateString() : '—'}
              </Text>
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 24, maxWidth: 800, alignSelf: 'center', width: '100%' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: '700' },
  periodRow: { flexDirection: 'row', gap: 8 },
  periodBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1, cursor: 'pointer' },
  table: { borderWidth: 1, borderRadius: 12, overflow: 'hidden' },
  tableHeader: {
    flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 12,
    borderBottomWidth: 1,
  },
  colHeader: { flex: 1, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow: { flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1 },
  cell: { flex: 1, fontSize: 13 },
});
